import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import Tooltip from "@/components/core/tooltip";
import { IconDragDrop, IconFileTypePdf, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";

interface CaseFileUploadModalProps {
	initialFiles: File[];
	onSubmit: (value: File[]) => Promise<void>;
}

export default function CaseFileUploadModal({ initialFiles, onSubmit }: CaseFileUploadModalProps) {
	const [files, setFiles] = useState<File[]>(initialFiles);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const modal = useModal();
	const { getRootProps, getInputProps } = useDropzone({
		accept: { "application/pdf": [], "image/*": [] },
		onDrop: (acceptedFiles) => {
			setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
		},
	});

	const handleDelete = (index: number) => {
		setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
	};

	const handleSubmit = () => {
		setIsSubmitting(true);
		onSubmit(files);
	};

	const handleClearList = () => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Are you sure?",
			description: "Do you really want to clear all files?",
			onConfirm: async () => {
				setFiles([]);
				modal.closeModal(modalId);
			},
		});
	};

	return (
		<div className="flex flex-col w-full gap-4">
			<div
				{...getRootProps({
					className: "dropzone inline-flex flex-col justify-center items-center w-full min-h-[240px] rounded-md border border-border/50",
				})}
			>
				<input {...getInputProps()} />
				<div className="inline-flex flex-col justify-center items-center h-full w-auto gap-2">
					<div className="inline-flex justify-center items-center self-center w-[100px] h-[100px] p-2 rounded-full border border-border/30">
						<div className="inline-flex justify-center items-center self-center w-full h-full p-2 rounded-full border border-border/50">
							<div className="inline-flex justify-center items-center self-center w-full h-full p-2 rounded-full border border-border/70">
								<div className="inline-flex justify-center items-center self-center w-full h-full rounded-full bg-border/50 text-foreground">
									<IconDragDrop size={24} stroke={1.5} />
								</div>
							</div>
						</div>
					</div>
					<p className="text-center text-base text-foreground/50">Drag 'n' drop some files here, or click to select files</p>
				</div>
			</div>

			<div className="flex flex-col w-full gap-2">
				<div className="flex flex-col w-full">
					<span className="font-bold text-sm text-foreground-dark">Added ({files.length}) file/s</span>
					<span className="relative inline-flex text-sm text-foreground/70">
						Review the uploaded files carefully before confirming. Drag 'n' drop files on the drop zone above to add more files.
					</span>
				</div>
				<div className="flex flex-col w-full rounded-md border border-border/50">
					{files.map((file, index) => (
						<div key={index} className="flex flex-col w-full p-2 gap-2 border-b last:border-b-0 border-border/50">
							<div className="flex flex-col w-full gap-2">
								<div className="flex flex-row items-center w-full gap-2">
									<div className="inline-flex justify-center items-center w-[32px] h-[32px] rounded-md bg-red-500 text-white">
										<IconFileTypePdf size={18} stroke={1.5} />
									</div>
									<div className="inline-flex flex-col w-auto">
										<span className="font-semibold text-sm text-foreground-dark">{file.name}</span>
										<span className="text-sm text-foreground/70">{file.size}</span>
									</div>
									<div className="inline-flex w-auto gap-3 ml-auto">
										<div className="inline-flex flex-row w-auto gap-1">
											<Tooltip content="Remove" side="top">
												<Button size="icon" variant="danger" type="button" onClick={() => handleDelete(index)}>
													<IconTrash size={16} stroke={2} />
												</Button>
											</Tooltip>
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="flex flex-row justify-between w-full">
				<Button type="button" className="inline-flex w-auto" variant="danger" onClick={() => handleClearList()}>
					Clear Files
				</Button>
				<Button disabled={isSubmitting} className="inline-flex w-auto" type="submit" onClick={() => handleSubmit()}>
					Save
				</Button>
			</div>
		</div>
	);
}
